import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useClients } from "@/lib/hooks";
import { eurFmt } from "@/lib/constants";
import {
  INVOICE_STATUS, PLACEHOLDERS, TEMPLATE_CATEGORIES, buildContext, fillTemplate, fmtDatePt,
  guessColumn, matchClient, monthLabel, parseAmount, parseCsv, parseDate, todayIso,
} from "@/lib/billing";
import { PageHeader } from "@/components/PageHeader";
import { EmailComposerDialog } from "@/components/EmailComposerDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, CheckCircle2, Clock, FileText, FileUp, Mail, MailWarning, Pencil, Plus,
  Receipt, RotateCcw, Search, Trash2, Wallet,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = [];
const NONE = "__none__";

function addDays(iso, days) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function Kpi({ icon: Icon, label, value, sub, tone = "primary" }) {
  const tones = {
    primary: "bg-primary/15 text-primary",
    red: "bg-red-500/10 text-red-500",
    amber: "bg-amber-500/10 text-amber-600",
    green: "bg-emerald-500/10 text-emerald-600",
  };
  return (
    <Card className="border-border p-5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-poppins text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-0.5 font-poppins text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function StatusCell({ invoice }) {
  const meta = INVOICE_STATUS[invoice.status];
  let detail = "";
  if (invoice.status === "EM_ATRASO") detail = `há ${invoice.daysOverdue} ${invoice.daysOverdue === 1 ? "dia" : "dias"}`;
  else if (invoice.status === "POR_PAGAR" && invoice.daysUntilDue != null) {
    detail = invoice.daysUntilDue === 0 ? "vence hoje" : `vence em ${invoice.daysUntilDue} ${invoice.daysUntilDue === 1 ? "dia" : "dias"}`;
  } else if (invoice.status === "PAGA") detail = `a ${fmtDatePt(invoice.paidDate)}`;
  return (
    <div>
      <Badge className={`rounded-full text-[10px] ${meta.className}`}>{meta.label}</Badge>
      {detail && <div className="mt-0.5 text-[11px] text-muted-foreground">{detail}</div>}
    </div>
  );
}

// ----------------------------- Fatura (criar/editar) -----------------------------
function InvoiceDialog({ state, onClose, clients, onSaved }) {
  const editing = state?.invoice;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const open = Boolean(state);

  if (open && !form) {
    const today = todayIso();
    setForm(editing
      ? { clientId: editing.clientId, number: editing.number, issueDate: editing.issueDate, dueDate: editing.dueDate || "", amount: String(editing.amount ?? ""), period: editing.period || "", notes: editing.notes || "" }
      : { clientId: state.clientId || "", number: "", issueDate: today, dueDate: addDays(today, 30), amount: state.amount ? String(state.amount) : "", period: state.period || "", notes: "" });
  }
  if (!open && form) setForm(null);

  const save = async () => {
    if (!form.clientId) return toast.error("Escolhe o cliente");
    if (!form.number.trim()) return toast.error("Indica o número da fatura");
    setSaving(true);
    try {
      const payload = { ...form, amount: parseAmount(form.amount) };
      if (editing) await api.put(`/billing/invoices/${editing.id}`, payload);
      else await api.post("/billing/invoices", payload);
      toast.success(editing ? "Fatura atualizada" : "Fatura registada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-poppins">{editing ? `Editar fatura ${editing.number}` : "Registar fatura"}</DialogTitle>
          <DialogDescription>Os dados da fatura emitida no Ebiz.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-sm">Cliente</Label>
              <Select value={form.clientId || undefined} onValueChange={(v) => setForm({ ...form, clientId: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Escolher cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Nº da fatura</Label><Input className="mt-1" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="FT 2026/123" /></div>
            <div><Label className="text-sm">Valor (€)</Label><Input className="mt-1" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" /></div>
            <div><Label className="text-sm">Data de emissão</Label><Input className="mt-1" type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} /></div>
            <div><Label className="text-sm">Data de vencimento</Label><Input className="mt-1" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="text-sm">Notas</Label><Input className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90">{saving ? "A guardar..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ invoice, onClose, onSaved }) {
  const [paidDate, setPaidDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/billing/invoices/${invoice.id}/pay`, { paidDate });
      toast.success(`Fatura ${invoice.number} marcada como paga`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={Boolean(invoice)} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-poppins">Marcar como paga</DialogTitle>
          <DialogDescription>{invoice ? `${invoice.number} · ${invoice.clientName} · ${eurFmt.format(invoice.amount)}` : ""}</DialogDescription>
        </DialogHeader>
        <div><Label className="text-sm">Data do pagamento</Label><Input className="mt-1" type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !paidDate} className="bg-primary text-black hover:bg-primary/90">Confirmar pagamento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------- Faturas -----------------------------
const FILTERS = [
  { key: "TODAS", label: "Todas" },
  { key: "POR_RECEBER", label: "Por receber" },
  { key: "EM_ATRASO", label: "Em atraso" },
  { key: "POR_PAGAR", label: "Dentro do prazo" },
  { key: "SEM_EMAIL", label: "Sem email enviado" },
  { key: "PAGA", label: "Pagas" },
];

function InvoicesTab({ invoices, clientsById, onEdit, onPay, onEmail, refresh }) {
  const [filter, setFilter] = useState("POR_RECEBER");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => ({
    TODAS: invoices.length,
    POR_RECEBER: invoices.filter((i) => i.status !== "PAGA").length,
    EM_ATRASO: invoices.filter((i) => i.status === "EM_ATRASO").length,
    POR_PAGAR: invoices.filter((i) => i.status === "POR_PAGAR").length,
    SEM_EMAIL: invoices.filter((i) => i.status !== "PAGA" && i.emailCount === 0).length,
    PAGA: invoices.filter((i) => i.status === "PAGA").length,
  }), [invoices]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices
      .filter((i) => {
        if (filter === "POR_RECEBER") return i.status !== "PAGA";
        if (filter === "SEM_EMAIL") return i.status !== "PAGA" && i.emailCount === 0;
        if (filter === "TODAS") return true;
        return i.status === filter;
      })
      .filter((i) => !q || i.number.toLowerCase().includes(q) || i.clientName.toLowerCase().includes(q))
      .sort((a, b) => {
        const rank = { EM_ATRASO: 0, POR_PAGAR: 1, PAGA: 2 };
        return rank[a.status] - rank[b.status] || (a.dueDate || "9").localeCompare(b.dueDate || "9");
      });
  }, [invoices, filter, search]);

  const unpay = async (invoice) => {
    try {
      await api.post(`/billing/invoices/${invoice.id}/unpay`);
      toast.success(`Pagamento da fatura ${invoice.number} anulado`);
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (invoice) => {
    if (!window.confirm(`Eliminar a fatura ${invoice.number} do CRM? (Não mexe no Ebiz.)`)) return;
    try {
      await api.delete(`/billing/invoices/${invoice.id}`);
      toast.success("Fatura eliminada");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === f.key ? "border-primary bg-primary text-black" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
            >
              {f.label} <span className="tabular-nums opacity-70">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <div className="relative lg:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cliente ou nº de fatura" className="pl-9" />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
          {invoices.length === 0 ? "Ainda não há faturas. Regista uma ou importa o ficheiro do Ebiz." : "Nenhuma fatura neste filtro."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-poppins">Fatura</th>
                <th className="py-2 pr-3 font-poppins">Cliente</th>
                <th className="py-2 pr-3 font-poppins">Vencimento</th>
                <th className="py-2 pr-3 text-right font-poppins">Valor</th>
                <th className="py-2 pr-3 font-poppins">Estado</th>
                <th className="py-2 pr-3 font-poppins">Emails</th>
                <th className="py-2 text-right font-poppins">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-3">
                    <div className="font-medium">{inv.number}</div>
                    <div className="text-[11px] text-muted-foreground">emitida {fmtDatePt(inv.issueDate)}</div>
                  </td>
                  <td className="py-3 pr-3 font-medium">{inv.clientName}</td>
                  <td className="py-3 pr-3 tabular-nums">{fmtDatePt(inv.dueDate)}</td>
                  <td className="py-3 pr-3 text-right font-poppins font-semibold tabular-nums">{eurFmt.format(inv.amount || 0)}</td>
                  <td className="py-3 pr-3"><StatusCell invoice={inv} /></td>
                  <td className="py-3 pr-3">
                    {inv.emailCount > 0 ? (
                      <div className="text-xs">
                        <div className="font-medium">{inv.emailCount} {inv.emailCount === 1 ? "enviado" : "enviados"}</div>
                        <div className="text-[11px] text-muted-foreground">último {fmtDatePt(inv.lastEmailAt)}</div>
                      </div>
                    ) : inv.status !== "PAGA" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600"><MailWarning className="h-3.5 w-3.5" />Nenhum</span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3">
                    <div className="flex justify-end gap-1">
                      <IconButton title="Preparar email" onClick={() => onEmail({ client: clientsById[inv.clientId], invoice: inv })}><Mail className="h-4 w-4" /></IconButton>
                      {inv.status === "PAGA"
                        ? <IconButton title="Anular pagamento" onClick={() => unpay(inv)}><RotateCcw className="h-4 w-4" /></IconButton>
                        : <IconButton title="Marcar como paga" onClick={() => onPay(inv)} tone="green"><CheckCircle2 className="h-4 w-4" /></IconButton>}
                      <IconButton title="Editar" onClick={() => onEdit(inv)}><Pencil className="h-4 w-4" /></IconButton>
                      <IconButton title="Eliminar" onClick={() => remove(inv)} tone="red"><Trash2 className="h-4 w-4" /></IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function IconButton({ title, onClick, children, tone }) {
  const tones = {
    green: "hover:bg-emerald-500/10 hover:text-emerald-600",
    red: "hover:bg-destructive/10 hover:text-destructive",
  };
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`rounded-md p-1.5 text-muted-foreground transition-colors ${tones[tone] || "hover:bg-muted hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

// ----------------------------- Avenças por faturar -----------------------------
function RetainersTab({ summary, clientsById, onRegister, onEmail }) {
  const rows = summary?.toInvoice || EMPTY;
  return (
    <Card className="p-5">
      <div className="mb-4">
        <h3 className="font-poppins font-semibold">Avenças por faturar em {monthLabel(summary?.month)}</h3>
        <p className="text-xs text-muted-foreground">Clientes ativos com avença que ainda não têm fatura registada deste mês.</p>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground">Todas as avenças deste mês já têm fatura registada.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.clientId} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{r.clientName}</div>
                <div className="text-xs text-muted-foreground">Avença {eurFmt.format(r.retainerValue)}/mês</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => onEmail({ client: clientsById[r.clientId], invoice: null, period: summary.month })}>
                <Mail className="mr-1.5 h-4 w-4" />Email
              </Button>
              <Button size="sm" onClick={() => onRegister({ clientId: r.clientId, amount: r.retainerValue, period: summary.month })} className="bg-primary text-black hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" />Registar fatura
              </Button>
            </div>
          ))}
          <div className="pt-2 text-right text-sm text-muted-foreground">
            Total por faturar: <span className="font-poppins font-semibold text-foreground">{eurFmt.format(summary.toInvoiceTotal)}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

// ----------------------------- Modelos -----------------------------
const SAMPLE_CONTEXT = buildContext({
  client: { id: "x", name: "AMBikes" },
  recipient: { name: "Ana Martins" },
  invoice: { number: "FT 2026/123", amount: 850, issueDate: "2026-09-01", dueDate: "2026-09-30", daysOverdue: 5, period: "2026-09", clientId: "x", status: "EM_ATRASO" },
  allInvoices: [{ clientId: "x", amount: 850, status: "EM_ATRASO" }, { clientId: "x", amount: 400, status: "POR_PAGAR" }],
});

function TemplateDialog({ state, onClose, onSaved }) {
  const editing = state?.template;
  const open = Boolean(state);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [focus, setFocus] = useState("body");
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);

  if (open && !form) setForm(editing ? { name: editing.name, category: editing.category, subject: editing.subject, body: editing.body } : { name: "", category: "GERAL", subject: "", body: "Olá {contacto},\n\n\n\nObrigado,\nI AM Social" });
  if (!open && form) setForm(null);

  const insert = (key) => {
    const token = `{${key}}`;
    const field = focus === "subject" ? "subject" : "body";
    const el = field === "subject" ? subjectRef.current : bodyRef.current;
    const value = form[field];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setForm({ ...form, [field]: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) return toast.error("Preenche o nome, o assunto e a mensagem");
    setSaving(true);
    try {
      if (editing) await api.put(`/billing/templates/${editing.id}`, form);
      else await api.post("/billing/templates", form);
      toast.success("Modelo guardado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-poppins">{editing ? "Editar modelo" : "Novo modelo de email"}</DialogTitle>
          <DialogDescription>Clica num campo para o inserir onde está o cursor. Ao usar o modelo, é trocado pelos dados reais.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Nome</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div>
                  <Label className="text-sm">Tipo</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TEMPLATE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-sm">Assunto</Label><Input ref={subjectRef} onFocus={() => setFocus("subject")} className="mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
              <div><Label className="text-sm">Mensagem</Label><Textarea ref={bodyRef} onFocus={() => setFocus("body")} className="mt-1 min-h-[220px] text-sm" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map((p) => (
                  <button key={p.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(p.key)} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary" title={p.label}>
                    {`{${p.key}}`}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Pré-visualização (dados de exemplo)</div>
              <div className="mb-3 font-medium">{fillTemplate(form.subject, SAMPLE_CONTEXT) || "—"}</div>
              <div className="whitespace-pre-wrap text-sm text-muted-foreground">{fillTemplate(form.body, SAMPLE_CONTEXT)}</div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90">Guardar modelo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesTab({ templates, refresh }) {
  const [dialog, setDialog] = useState(null);
  const remove = async (t) => {
    if (!window.confirm(`Eliminar o modelo "${t.name}"?`)) return;
    await api.delete(`/billing/templates/${t.id}`);
    toast.success("Modelo eliminado");
    refresh();
  };
  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(`${t.subject}\n\n${t.body}`);
      toast.success("Modelo copiado (com os campos por preencher)");
    } catch { toast.error("Não foi possível copiar"); }
  };
  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Textos prontos a usar. Os campos entre chavetas são trocados pelos dados do cliente e da fatura.</p>
        <Button onClick={() => setDialog({})} className="shrink-0 bg-primary text-black hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" />Novo modelo</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {templates.map((t) => (
          <Card key={t.id} className="flex flex-col p-5">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <h3 className="font-poppins font-semibold">{t.name}</h3>
                <Badge variant="secondary" className="mt-1 rounded-full text-[10px]">{TEMPLATE_CATEGORIES[t.category] || t.category}</Badge>
              </div>
              <div className="flex gap-1">
                <IconButton title="Copiar" onClick={() => copy(t)}><FileText className="h-4 w-4" /></IconButton>
                <IconButton title="Editar" onClick={() => setDialog({ template: t })}><Pencil className="h-4 w-4" /></IconButton>
                <IconButton title="Eliminar" onClick={() => remove(t)} tone="red"><Trash2 className="h-4 w-4" /></IconButton>
              </div>
            </div>
            <div className="text-sm font-medium">{t.subject}</div>
            <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-xs text-muted-foreground">{t.body}</p>
          </Card>
        ))}
      </div>
      <TemplateDialog state={dialog} onClose={() => setDialog(null)} onSaved={refresh} />
    </>
  );
}

// ----------------------------- Importar -----------------------------
const IMPORT_FIELDS = [
  { key: "number", label: "Nº da fatura", required: true, guesses: ["numero", "documento", "ndoc", "fatura"] },
  { key: "client", label: "Cliente (nome ou NIF)", required: true, guesses: ["cliente", "entidade", "nome", "nif", "contribuinte"] },
  { key: "issueDate", label: "Data de emissão", required: true, guesses: ["emissao", "datadoc", "datadocumento", "data"] },
  { key: "dueDate", label: "Data de vencimento", guesses: ["vencimento", "venc", "limite"] },
  { key: "amount", label: "Valor total", required: true, guesses: ["total", "valor", "montante"] },
  { key: "paidDate", label: "Data de pagamento (se já paga)", guesses: ["pagamento", "liquidacao", "recebimento"] },
];

function ImportTab({ clients, refresh }) {
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState({});
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const onFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    const buffer = await picked.arrayBuffer();
    let text = new TextDecoder("utf-8").decode(buffer);
    if (text.includes("�")) text = new TextDecoder("windows-1252").decode(buffer);
    const parsed = parseCsv(text);
    if (!parsed.headers.length || !parsed.rows.length) {
      toast.error("Não encontrei dados no ficheiro. Confirma que é um CSV com cabeçalho.");
      return;
    }
    const guessed = {};
    IMPORT_FIELDS.forEach((f) => { guessed[f.key] = guessColumn(parsed.headers, f.guesses) || NONE; });
    setFile({ name: picked.name, ...parsed });
    setMapping(guessed);
    setOverrides({});
    setResult(null);
  };

  const cell = (row, key) => (mapping[key] && mapping[key] !== NONE ? row[Number(mapping[key])] ?? "" : "");

  const rows = useMemo(() => {
    if (!file) return EMPTY;
    return file.rows.map((row, index) => {
      const clientRaw = cell(row, "client");
      const matched = matchClient(clientRaw, clients);
      const clientId = overrides[index] ?? matched?.id ?? "";
      const out = {
        index,
        clientRaw,
        clientId,
        autoMatched: Boolean(matched) && overrides[index] === undefined,
        number: cell(row, "number").trim(),
        issueDate: parseDate(cell(row, "issueDate")),
        dueDate: parseDate(cell(row, "dueDate")),
        amount: parseAmount(cell(row, "amount")),
        paidDate: parseDate(cell(row, "paidDate")),
      };
      out.valid = Boolean(out.number && out.clientId && out.issueDate);
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, mapping, overrides, clients]);

  const valid = rows.filter((r) => r.valid);
  const missingRequired = IMPORT_FIELDS.filter((f) => f.required && (!mapping[f.key] || mapping[f.key] === NONE));

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/billing/import", {
        rows: valid.map(({ clientId, number, issueDate, dueDate, amount, paidDate }) => ({ clientId, number, issueDate, dueDate, amount, paidDate })),
      });
      setResult(data);
      toast.success(`${data.created} faturas novas · ${data.updated} atualizadas`);
      refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <h3 className="font-poppins font-semibold">Importar faturas do Ebiz</h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          No Ebiz, exporta a listagem de faturas em CSV (se sair em Excel, abre-o e usa &ldquo;Guardar como&rdquo; → CSV).
          Faturas que já existam no CRM com o mesmo número são atualizadas; se o ficheiro trouxer a data de pagamento, ficam marcadas como pagas.
        </p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-primary/50 px-4 py-3 text-sm font-medium text-primary transition-colors hover:bg-primary/5">
          <FileUp className="h-4 w-4" /> {file ? `Trocar ficheiro (${file.name})` : "Escolher ficheiro CSV"}
          <input type="file" accept=".csv,text/csv,.txt" className="sr-only" onChange={onFile} />
        </label>
      </Card>

      {file && (
        <Card className="p-5">
          <h3 className="mb-1 font-poppins font-semibold">1. Que coluna é o quê?</h3>
          <p className="mb-4 text-xs text-muted-foreground">Tentei adivinhar pelo nome das colunas. Confirma e corrige se for preciso.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {IMPORT_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-sm">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                <Select value={mapping[f.key] || NONE} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— não usar —</SelectItem>
                    {file.headers.map((h, i) => <SelectItem key={`${h}-${i}`} value={String(i)}>{h || `Coluna ${i + 1}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {file && missingRequired.length === 0 && (
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-poppins font-semibold">2. Rever e importar</h3>
              <p className="text-xs text-muted-foreground">
                {valid.length} de {rows.length} linhas prontas. Linhas sem cliente reconhecido: escolhe o cliente à mão ou ficam de fora.
              </p>
            </div>
            <Button onClick={runImport} disabled={importing || valid.length === 0} className="bg-primary text-black hover:bg-primary/90">
              <FileUp className="mr-1.5 h-4 w-4" />{importing ? "A importar..." : `Importar ${valid.length} faturas`}
            </Button>
          </div>
          <div className="max-h-[480px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-poppins">Nº</th>
                  <th className="py-2 pr-3 font-poppins">No ficheiro</th>
                  <th className="py-2 pr-3 font-poppins">Cliente no CRM</th>
                  <th className="py-2 pr-3 font-poppins">Emissão</th>
                  <th className="py-2 pr-3 font-poppins">Vencimento</th>
                  <th className="py-2 pr-3 text-right font-poppins">Valor</th>
                  <th className="py-2 font-poppins">Paga a</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 300).map((r) => (
                  <tr key={r.index} className={`border-b border-border/60 ${r.valid ? "" : "bg-amber-500/5"}`}>
                    <td className="py-2 pr-3 font-medium">{r.number || <span className="text-destructive">em falta</span>}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{r.clientRaw || "—"}</td>
                    <td className="py-2 pr-3">
                      <Select value={r.clientId || NONE} onValueChange={(v) => setOverrides({ ...overrides, [r.index]: v === NONE ? "" : v })}>
                        <SelectTrigger className={`h-8 min-w-[160px] text-xs ${r.clientId ? "" : "border-amber-500/60"}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— não importar —</SelectItem>
                          {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{r.issueDate ? fmtDatePt(r.issueDate) : <span className="text-destructive">inválida</span>}</td>
                    <td className="py-2 pr-3 tabular-nums">{fmtDatePt(r.dueDate)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{eurFmt.format(r.amount)}</td>
                    <td className="py-2 tabular-nums">{r.paidDate ? fmtDatePt(r.paidDate) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 300 && <p className="pt-2 text-center text-xs text-muted-foreground">A mostrar as primeiras 300 linhas; todas as válidas são importadas.</p>}
          </div>
          {result && (
            <div className="mt-4 rounded-lg bg-muted/50 p-3 text-sm">
              <b>{result.created}</b> novas · <b>{result.updated}</b> atualizadas · <b>{result.skipped}</b> ignoradas
              {result.errors?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">{result.errors.map((err) => <li key={err}>{err}</li>)}</ul>}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ----------------------------- Página -----------------------------
export default function Faturacao() {
  const qc = useQueryClient();
  const { data: invoices = EMPTY } = useQuery({ queryKey: ["billing-invoices"], queryFn: async () => (await api.get("/billing/invoices")).data });
  const { data: summary } = useQuery({ queryKey: ["billing-summary"], queryFn: async () => (await api.get("/billing/summary")).data });
  const { data: templates = EMPTY } = useQuery({ queryKey: ["billing-templates"], queryFn: async () => (await api.get("/billing/templates")).data });
  const { data: clients = EMPTY } = useClients();
  const clientsById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const sortedClients = useMemo(() => [...clients].sort((a, b) => a.name.localeCompare(b.name)), [clients]);

  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["billing-invoices"] });
    qc.invalidateQueries({ queryKey: ["billing-summary"] });
  };
  const refreshTemplates = () => qc.invalidateQueries({ queryKey: ["billing-templates"] });

  return (
    <div>
      <PageHeader
        title="Faturação"
        subtitle="Faturas emitidas no Ebiz: o que está pago, o que falta receber e os emails aos clientes."
        actions={<Button onClick={() => setInvoiceDialog({})} className="bg-primary text-black hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" />Registar fatura</Button>}
      />

      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Wallet} label="Por receber" value={eurFmt.format(summary?.outstanding.total || 0)} sub={`${summary?.outstanding.count || 0} faturas por pagar`} />
        <Kpi icon={AlertTriangle} label="Em atraso" value={eurFmt.format(summary?.overdue.total || 0)} sub={`${summary?.overdue.count || 0} faturas`} tone={summary?.overdue.count ? "red" : "green"} />
        <Kpi icon={Clock} label="Vence nos próximos 7 dias" value={eurFmt.format(summary?.dueSoon.total || 0)} sub={`${summary?.dueSoon.count || 0} faturas`} tone="amber" />
        <Kpi icon={CheckCircle2} label="Recebido este mês" value={eurFmt.format(summary?.paidThisMonth.total || 0)} sub={`${summary?.paidThisMonth.count || 0} faturas pagas`} tone="green" />
      </div>

      {summary && (summary.unpaidWithoutEmail > 0 || summary.toInvoice.length > 0) && (
        <div className="mb-6 flex flex-wrap gap-2">
          {summary.unpaidWithoutEmail > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
              <MailWarning className="h-3.5 w-3.5" />{summary.unpaidWithoutEmail} {summary.unpaidWithoutEmail === 1 ? "fatura por pagar ainda sem email" : "faturas por pagar ainda sem email"}
            </span>
          )}
          {summary.toInvoice.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Receipt className="h-3.5 w-3.5" />{summary.toInvoice.length} {summary.toInvoice.length === 1 ? "avença por faturar" : "avenças por faturar"} este mês
            </span>
          )}
        </div>
      )}

      <Tabs defaultValue="faturas">
        <TabsList className="mb-5 h-auto flex-wrap">
          <TabsTrigger value="faturas">Faturas</TabsTrigger>
          <TabsTrigger value="avencas">Avenças por faturar{summary?.toInvoice.length ? ` (${summary.toInvoice.length})` : ""}</TabsTrigger>
          <TabsTrigger value="modelos">Modelos de email</TabsTrigger>
          <TabsTrigger value="importar">Importar do Ebiz</TabsTrigger>
        </TabsList>
        <TabsContent value="faturas">
          <InvoicesTab invoices={invoices} clientsById={clientsById} onEdit={(invoice) => setInvoiceDialog({ invoice })} onPay={setPayFor} onEmail={setEmailFor} refresh={refresh} />
        </TabsContent>
        <TabsContent value="avencas">
          <RetainersTab summary={summary} clientsById={clientsById} onRegister={(prefill) => setInvoiceDialog(prefill)} onEmail={setEmailFor} />
        </TabsContent>
        <TabsContent value="modelos">
          <TemplatesTab templates={templates} refresh={refreshTemplates} />
        </TabsContent>
        <TabsContent value="importar">
          <ImportTab clients={sortedClients} refresh={refresh} />
        </TabsContent>
      </Tabs>

      <InvoiceDialog state={invoiceDialog} onClose={() => setInvoiceDialog(null)} clients={sortedClients} onSaved={refresh} />
      <PayDialog key={payFor?.id || "none"} invoice={payFor} onClose={() => setPayFor(null)} onSaved={refresh} />
      <EmailComposerDialog
        open={Boolean(emailFor)}
        onOpenChange={(next) => { if (!next) setEmailFor(null); }}
        client={emailFor?.client}
        invoice={emailFor?.invoice}
        period={emailFor?.period}
        templates={templates}
        allInvoices={invoices}
        onLogged={refresh}
      />
    </div>
  );
}
